import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import * as Models from 'Models/Entities';

export interface IParkingReservationVehicleEntityAttributes extends IModelAttributes {
	parkingReservationId: string;
	parkingReservation: Models.ParkingReservationEntity | Models.IParkingReservationEntityAttributes;
	parkingVehicleTypeId: string;
	parkingVehicleType?: Models.ParkingVehicleTypeEntity | Models.IParkingVehicleTypeEntityAttributes;
	registration?: string;
	make?: string;
	model?: string;
	startDate: string;
	endDate: string;
	dailyRate: number;
	subTotal: number;
	parkingPricingTierId?: string;
	parkingPricingTier?: Models.ParkingPricingTierEntity | Models.IParkingPricingTierEntityAttributes;
}

@entity('ParkingReservationVehicleEntity', 'Parking Reservation Vehicle')
export default class ParkingReservationVehicleEntity
	extends Model
	implements IParkingReservationVehicleEntityAttributes
{
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute()
	public parkingReservationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public parkingReservation: Models.ParkingReservationEntity;

	@observable
	@attribute()
	public parkingVehicleTypeId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public parkingVehicleType?: Models.ParkingVehicleTypeEntity;

	@observable
	@attribute<ParkingReservationVehicleEntity, string>()
	public registration?: string;

	@observable
	@attribute<ParkingReservationVehicleEntity, string>()
	public make?: string;

	@observable
	@attribute<ParkingReservationVehicleEntity, string>()
	public model?: string;

	@observable
	@attribute<ParkingReservationVehicleEntity, string>()
	public startDate: string;

	@observable
	@attribute<ParkingReservationVehicleEntity, string>()
	public endDate: string;

	@observable
	@attribute<ParkingReservationVehicleEntity, number>()
	public dailyRate: number;

	@observable
	@attribute<ParkingReservationVehicleEntity, number>()
	public subTotal: number;

	@observable
	@attribute()
	public parkingPricingTierId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public parkingPricingTier?: Models.ParkingPricingTierEntity;

	constructor(attributes?: Partial<IParkingReservationVehicleEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IParkingReservationVehicleEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.parkingReservationId)) {
				this.parkingReservationId = attributes.parkingReservationId;
			}
			if (isNotNullOrUndefined(attributes.parkingReservation)) {
				if (attributes.parkingReservation === null) {
					this.parkingReservation = attributes.parkingReservation;
				} else if (attributes.parkingReservation instanceof Models.ParkingReservationEntity) {
					this.parkingReservation = attributes.parkingReservation;
					this.parkingReservationId = attributes.parkingReservation.id;
				} else {
					this.parkingReservation = new Models.ParkingReservationEntity(attributes.parkingReservation);
					this.parkingReservationId = this.parkingReservation.id;
				}
			}
			if (isNotNullOrUndefined(attributes.parkingVehicleTypeId)) {
				this.parkingVehicleTypeId = attributes.parkingVehicleTypeId;
			}
			if (isNotNullOrUndefined(attributes.parkingVehicleType)) {
				if (attributes.parkingVehicleType === null) {
					this.parkingVehicleType = attributes.parkingVehicleType;
				} else if (attributes.parkingVehicleType instanceof Models.ParkingVehicleTypeEntity) {
					this.parkingVehicleType = attributes.parkingVehicleType;
					this.parkingVehicleTypeId = attributes.parkingVehicleType.id;
				} else {
					this.parkingVehicleType = new Models.ParkingVehicleTypeEntity(attributes.parkingVehicleType);
					this.parkingVehicleTypeId = this.parkingVehicleType.id;
				}
			}
			if (isNotNullOrUndefined(attributes.registration)) {
				this.registration = attributes.registration;
			}
			if (isNotNullOrUndefined(attributes.make)) {
				this.make = attributes.make;
			}
			if (isNotNullOrUndefined(attributes.model)) {
				this.model = attributes.model;
			}
			if (isNotNullOrUndefined(attributes.startDate)) {
				this.startDate = attributes.startDate;
			}
			if (isNotNullOrUndefined(attributes.endDate)) {
				this.endDate = attributes.endDate;
			}
			if (isNotNullOrUndefined(attributes.dailyRate)) {
				this.dailyRate = attributes.dailyRate;
			}
			if (isNotNullOrUndefined(attributes.subTotal)) {
				this.subTotal = attributes.subTotal;
			}
			if (isNotNullOrUndefined(attributes.parkingPricingTierId)) {
				this.parkingPricingTierId = attributes.parkingPricingTierId;
			}
			if (isNotNullOrUndefined(attributes.parkingPricingTier)) {
				if (attributes.parkingPricingTier === null) {
					this.parkingPricingTier = attributes.parkingPricingTier;
				} else if (attributes.parkingPricingTier instanceof Models.ParkingPricingTierEntity) {
					this.parkingPricingTier = attributes.parkingPricingTier;
					this.parkingPricingTierId = attributes.parkingPricingTier.id;
				} else {
					this.parkingPricingTier = new Models.ParkingPricingTierEntity(attributes.parkingPricingTier);
					this.parkingPricingTierId = this.parkingPricingTier.id;
				}
			}
		}
	}
}
